import React from 'react';
import { Seo } from '../shared';
import KnowledgeHubLayout from '../explore/KnowledgeHubLayout';
import DiscoverySection from '../explore/DiscoverySection';
import OfficialCitation from '../explore/OfficialCitation';
import ArrivalStepOutline from './ArrivalStepOutline';
import CategoryGuidesSection from './CategoryGuidesSection';
import { ARRIVAL_HUB } from '../../data/arrivalKnowledge';
import { OFFICIAL_DOCUMENTS } from '../../data/officialDocuments';
import { NEPAL_MISSIONS_ABROAD } from '../../data/embassyDirectory';
import { LAST_VERIFIED } from '../../data/officialSources';
import '../../styles/explore.scss';
import '../../styles/guides.scss';

function ArrivalHubPage() {
	const hub = ARRIVAL_HUB;

	return (
		<KnowledgeHubLayout
			touristSection="plan"
			touristCurrent="Airport arrival"
			planStepId="arrival"
			hideRelated
			planBookingPanel
			seo={(
				<Seo
					title="Arriving in Nepal — Kathmandu Airport Guide (TIA)"
					path="/arrival"
					description="Complete Tribhuvan International Airport arrival guide — immigration, visa payment, customs, ATM, SIM, prepaid taxi to Thamel, scams to avoid, and Day 1 checklist. Official sources cited."
				/>
			)}
			hero={{
				image: hub.hero,
				eyebrow: 'Plan your trip',
				title: hub.title,
				lead: 'Everything from plane landing to your hotel door — immigration, visa counter, baggage, customs, money, SIM, taxi, and what to do on Day 1. Official rules cited; practical tips from experienced travelers.',
			}}
			quickFacts={hub.quickFacts}
			intro={{
				title: 'Your first hour in Nepal',
				text: 'This is one connected guide — not a wizard, not a redirect chain.',
				body: 'Follow the steps below in order. If you already have a visa, skip steps 3–5. Every section answers what a first-time visitor actually asks at the airport.',
			}}
			faqs={hub.faqs}
		>
			<div className="container" style={{ marginTop: '-1rem', marginBottom: '1.5rem' }}>
				<OfficialCitation
					sourceName={hub.primarySource.name}
					sourceUrl={hub.primarySource.url}
					lastVerified={hub.lastVerified || LAST_VERIFIED}
					subjectToChange
				>
					<p className="mb-0">Immigration procedures cited from TIA Immigration Office and Department of Immigration. Taxi fares and scam warnings are practical traveler guidance — not government statements.</p>
				</OfficialCitation>
			</div>

			<ArrivalStepOutline steps={hub.sections} />

			<DiscoverySection eyebrow="Step by step" title="From landing to your hotel">
				{hub.sections.map((s) => (
					<div key={s.id} id={s.id} className="info-card arrival-step-card" style={{ marginBottom: '1.25rem' }}>
						<div className="info-card__body">
							<span className="arrival-step-card__num">Step {s.step}</span>
							<h3 className="info-card__title">{s.question}</h3>
							<p className="info-card__text">{s.answer}</p>
							{s.table && (
								<table className="fee-table">
									<thead>
										<tr>
											<th>{s.id === 'taxi' ? 'Destination' : 'Duration'}</th>
											<th>{s.id === 'taxi' ? 'Prepaid fare' : 'Fee'}</th>
										</tr>
									</thead>
									<tbody>
										{s.table.map((row) => (
											<tr key={row.duration || row.destination}>
												<td>{row.duration || row.destination}</td>
												<td><strong>{row.fee}</strong></td>
											</tr>
										))}
									</tbody>
								</table>
							)}
							{s.tips && s.tips.length > 0 && (
								<div className="arrival-tips">
									<p className="arrival-tips__label">Traveler tips</p>
									<ul>
										{s.tips.map((t) => <li key={t}>{t}</li>)}
									</ul>
								</div>
							)}
							{s.source && (
								<p className="section-source-tag">
									Official: <a href={s.source.url} target="_blank" rel="noopener noreferrer">{s.source.name}</a>
								</p>
							)}
							{s.editorial && (
								<p className="section-source-tag section-source-tag--editorial">Practical guidance — verify current rates and facilities on arrival</p>
							)}
						</div>
					</div>
				))}
			</DiscoverySection>

			<DiscoverySection eyebrow="Peak season" title="Arriving in October or March?" alt>
				<ul className="arrival-persona-list">
					<li>October–November and March–April are peak seasons — immigration queues can take 60–90 minutes.</li>
					<li>Complete the online visa form at nepaliport.immigration.gov.np before landing to save time.</li>
					<li>Dashain (often October) and Holi (spring) may affect shop hours — check festival dates for your year.</li>
					<li>Prepaid taxi to Thamel costs roughly NPR 700–900. Buy NTC or Ncell SIM in arrivals if you need ride-apps.</li>
					<li>First day: rest, activate SIM, withdraw NPR — do not start a high-altitude trek the same day you land.</li>
				</ul>
			</DiscoverySection>

			<DiscoverySection eyebrow="Before you fly" title="Nepali embassies — top visitor countries">
				<div className="embassy-grid">
					{NEPAL_MISSIONS_ABROAD.slice(0, 6).map((e) => (
						<div key={e.country} className="embassy-card">
							<h4 className="embassy-card__country">{e.country}</h4>
							<p className="embassy-card__name">{e.name}</p>
							{e.address && <p className="embassy-card__meta">{e.address}</p>}
							{e.phone && <p className="embassy-card__meta">{e.phone}</p>}
							<p className="embassy-card__note">{e.visaNote}</p>
							<a href={e.url} target="_blank" rel="noopener noreferrer" className="embassy-card__link">Official website ↗</a>
						</div>
					))}
				</div>
				<p className="section-source-tag mt-3">
					VOA-ineligible nationalities must apply at an embassy before travel — confirm list at{' '}
					<a href="https://www.immigration.gov.np/en/page/visa-on-arrival" target="_blank" rel="noopener noreferrer">immigration.gov.np</a>
				</p>
			</DiscoverySection>

			<DiscoverySection eyebrow="Official resources" title="Government documents & forms" intro="Summarized for travelers — always consult the original for legal decisions." alt>
				<div className="official-links-grid">
					{OFFICIAL_DOCUMENTS.slice(0, 12).map((doc) => (
						<a key={doc.id} href={doc.url} target="_blank" rel="noopener noreferrer" className="official-link" title={doc.summary}>
							↗ {doc.title}
							<span className="official-link__meta">{doc.authority}</span>
						</a>
					))}
				</div>
			</DiscoverySection>

			<DiscoverySection eyebrow="Verify yourself" title="Official links for arrival">
				<div className="official-links-grid">
					{hub.officialLinks.map((link) => (
						<a key={link.url} href={link.url} target="_blank" rel="noopener noreferrer" className="official-link">
							↗ {link.label}
						</a>
					))}
				</div>
			</DiscoverySection>

			<CategoryGuidesSection
				category="arrival"
				title="Arrival & airport guides"
				intro="Focused articles on visa counters, customs, and your first day in Nepal."
				alt
			/>
		</KnowledgeHubLayout>
	);
}

export default ArrivalHubPage;
